import {
  Component,
  HostListener,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  inject,
  effect,
  TemplateRef,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { trigger, transition, style, animate, AnimationEvent } from '@angular/animations';
import { NavigationState } from 'src/app/store/navigation/navigation.state';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { ButtonComponent } from 'src/app/ui-kit/button/button.component';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { Color, SheetActionItem } from 'src/app/shared/types';
import { WavyDividerComponent } from 'src/app/ui-kit/wavy-divider/wavy-divider.component';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';
import { FocusTrapDirective } from 'src/app/ui-kit/a11y/focus-trap.directive';
/**
 * Bottom Sheet Component
 *
 * A global bottom sheet component that displays items from configuration.
 * Similar to global-nav-bar, it subscribes to state and renders items from config arrays.
 *
 * Features:
 * - Smooth slide in/out animations
 * - Backdrop overlay
 * - Click outside to close
 * - ESC key to close
 * - Body scroll lock when open
 * - Configuration-based rendering
 *
 * @example
 * // Use via NavigationState
 * this.nav.setSheet({
 *   visible: true,
 *   title: 'Menu',
 *   description: 'Select an option',
 *   content: [
 *     { label: 'Option 1', icon: 'star', route: '/route1' },
 *     { label: 'Option 2', icon: 'heart', action: () => console.log('clicked') }
 *   ]
 * });
 */
@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    IconComponent,
    ButtonComponent,
    CardComponent,
    WavyDividerComponent,
    IconButtonComponent,
    FocusTrapDirective,
  ],
  templateUrl: './bottom-sheet.component.html',
  styleUrls: ['./bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('sheet', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate(
          '300ms cubic-bezier(0.16, 1, 0.3, 1)',
          style({ transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate('250ms ease-in', style({ transform: 'translateY(100%)' })),
      ]),
    ]),
  ],
})
export class BottomSheetComponent implements OnDestroy {
  // --- Injected dependencies ---
  private readonly nav = inject(NavigationState);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  /**
   * Whether the bottom sheet is visible
   */
  visible = false;

  /**
   * Title of the sheet
   */
  title?: string;

  /**
   * Description text below the title
   */
  description?: string;

  /**
   * Content items to display
   */
  content: SheetActionItem[] | TemplateRef<unknown> | null = [];

  /**
   * Whether to show the "Done" button at the bottom of the sheet
   * @default false
   */
  showDoneButton?: boolean = false;

  /**
   * Text to display on the "Done" button
   * @default 'Done'
   */
  doneButtonText?: string = 'Done';

  /**
   * Color of the "Done" button
   * @default 'purple'
   */
  doneButtonColor?: Color = 'purple';

  /**
   * Whether the sheet should take up full height
   * @default false
   */
  fullHeight?: boolean = false;

  /**
   * Back button action
   */
  backButtonAction?: () => void;

  /**
   * Close button type
   */
  closeButtonType: 'close' | 'done' = 'close';

  /**
   * Internal flag to control DOM presence
   */
  showContainer = false;

  /**
   * Drag state variables
   */
  private isDragging = false;
  isDismissingViaDrag = false; // Public for template access
  private startY = 0;
  private currentY = 0;
  private translateY = 0;

  /**
   * Drag thresholds
   */
  private readonly DRAG_THRESHOLD = 100; // pixels to drag before dismissing
  private readonly VELOCITY_THRESHOLD = 0.5; // pixels per ms for quick swipe

  constructor() {
    // React to sheet configuration from signal state
    effect(() => {
      const sheet = this.nav.sheet();
      const wasVisible = this.visible;
      this.visible = sheet.visible;
      this.title = sheet.title;
      this.description = sheet.description;
      this.showDoneButton = sheet.showDoneButton ?? false;
      this.doneButtonText = sheet.doneButtonText;
      this.doneButtonColor = sheet.doneButtonColor;
      this.content = sheet.content || [];
      this.fullHeight = sheet.fullHeight ?? false;
      this.backButtonAction = sheet.backButtonAction;
      this.closeButtonType = sheet.closeButtonType ?? 'close';

      // Handle visibility changes
      if (this.visible) {
        // Opening or staying open (important for HMR)
        this.showContainer = true;
        this.toggleBodyScroll(true);
      } else {
        // Not visible - ensure sheet is hidden and body scroll is unlocked
        // This handles both closing and initialization/HMR scenarios
        this.toggleBodyScroll(false);
        // Only set showContainer to false after animation completes
        if (wasVisible) {
          // Will be set to false by animation done handler
        } else {
          // On init or HMR, no animation to wait for
          this.showContainer = false;
        }
      }

      this.cdr.markForCheck();
    });
  }

  /**
   * Check if content is an array of SheetActionItems
   */
  isActionItemArray(content: SheetActionItem[] | TemplateRef<unknown> | null): content is SheetActionItem[] {
    return (
      Array.isArray(content) &&
      content.length > 0 &&
      content[0]?.label !== undefined
    );
  }

  /**
   * Listen for ESC key press
   */
  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.visible) {
      this.handleClose();
    }
  }

  /**
   * Listen for global touch move (for drag)
   */
  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (this.isDragging) {
      this.onDragMove(event);
    }
  }

  /**
   * Listen for global touch end (for drag)
   */
  @HostListener('document:touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (this.isDragging) {
      this.onDragEnd(event);
    }
  }

  /**
   * Listen for global mouse move (for drag)
   */
  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.onDragMove(event);
    }
  }

  /**
   * Listen for global mouse up (for drag)
   */
  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isDragging) {
      this.onDragEnd(event);
    }
  }

  ngOnDestroy(): void {
    // Ensure body scroll is restored
    this.toggleBodyScroll(false);
  }

  /**
   * Handle animation completion
   */
  onAnimationDone(event: AnimationEvent): void {
    // When leave animation completes, remove from DOM
    if (event.toState === 'void' && !this.visible) {
      this.showContainer = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle close event
   */
  handleClose(): void {
    this.nav.closeSheet();
  }

  /**
   * Handle back button click
   */
  handleBack(): void {
    if (this.backButtonAction) {
      this.backButtonAction();
    }
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(): void {
    this.handleClose();
  }

  /**
   * Prevent click propagation from sheet content
   */
  onSheetClick(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Handle item click
   */
  handleItemClick(item: SheetActionItem): void {
    // First, close the sheet to start the animation
    this.handleClose();

    // Then execute the action after animation completes
    // Sheet leave animation is 250ms, so wait 300ms to ensure it's fully complete
    setTimeout(() => {
      if (item.action) {
        item.action();
      }
      if (item.route) {
        this.router.navigate([item.route]);
      }
    }, 0);
  }

  /**
   * Toggle body scroll lock
   */
  private toggleBodyScroll(lock: boolean): void {
    if (lock) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  /**
   * Handle touch/mouse start for drag gesture
   */
  onDragStart(event: TouchEvent | MouseEvent): void {
    this.isDragging = true;

    if (event instanceof TouchEvent) {
      this.startY = event.touches[0].clientY;
    } else {
      this.startY = event.clientY;
    }

    this.currentY = this.startY;
  }

  /**
   * Handle touch/mouse move for drag gesture
   */
  onDragMove(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) return;

    let clientY: number;
    if (event instanceof TouchEvent) {
      clientY = event.touches[0].clientY;
    } else {
      clientY = event.clientY;
    }

    const deltaY = clientY - this.startY;

    // Only allow dragging down, not up
    if (deltaY > 0) {
      this.translateY = deltaY;
      this.currentY = clientY;

      // Apply transform to the sheet
      const sheetElement = this.getSheetElement();
      if (sheetElement) {
        // Add resistance effect - the further you drag, the harder it gets
        const resistance = Math.max(0.3, 1 - deltaY / 500);
        const adjustedDelta = deltaY * resistance;

        sheetElement.style.transform = `translateY(${adjustedDelta}px)`;

        // Fade backdrop slightly as user drags
        const backdropOpacity = Math.max(0.3, 1 - deltaY / 400);
        const backdropElement = document.querySelector(
          '.bottom-sheet-backdrop'
        ) as HTMLElement;
        if (backdropElement) {
          backdropElement.style.opacity = backdropOpacity.toString();
        }
      }
    }
  }

  /**
   * Handle touch/mouse end for drag gesture
   */
  onDragEnd(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) return;

    const deltaY = this.translateY;
    const velocity = this.calculateVelocity();

    const sheetElement = this.getSheetElement();
    const backdropElement = document.querySelector(
      '.bottom-sheet-backdrop'
    ) as HTMLElement;

    // Determine if we should dismiss or snap back
    const shouldDismiss =
      deltaY > this.DRAG_THRESHOLD || velocity > this.VELOCITY_THRESHOLD;

    if (shouldDismiss) {
      // Set flag to bypass Angular exit animation
      this.isDismissingViaDrag = true;

      // Dismiss the sheet with fast animation from current position
      if (sheetElement) {
        sheetElement.style.transition =
          'transform 200ms ease-in, opacity 200ms ease-in';
        sheetElement.style.transform = 'translateY(100%)';
        sheetElement.style.opacity = '0';
      }
      if (backdropElement) {
        backdropElement.style.transition = 'opacity 200ms ease-in';
        backdropElement.style.opacity = '0';
      }

      // Close after animation completes
      setTimeout(() => {
        this.handleClose();
        this.resetDragState();
        // Reset flag after a delay to ensure Angular animation is skipped
        setTimeout(() => {
          this.isDismissingViaDrag = false;
        }, 50);
      }, 200);
    } else {
      // Snap back to original position
      if (sheetElement) {
        sheetElement.style.transition =
          'transform 250ms cubic-bezier(0.4, 0.0, 0.2, 1)';
        sheetElement.style.transform = 'translateY(0)';
      }
      if (backdropElement) {
        backdropElement.style.transition = 'opacity 250ms ease-out';
        backdropElement.style.opacity = '1';
      }

      // Reset after animation
      setTimeout(() => {
        this.resetDragState();
      }, 250);
    }

    this.isDragging = false;
  }

  /**
   * Calculate drag velocity for quick swipe detection
   */
  private calculateVelocity(): number {
    // Simple velocity calculation based on last movement
    return Math.abs(this.translateY) / 100; // Normalized velocity
  }

  /**
   * Get the sheet element
   */
  private getSheetElement(): HTMLElement | null {
    return document.querySelector('.bottom-sheet') as HTMLElement;
  }

  /**
   * Reset drag state and clear inline styles
   */
  private resetDragState(): void {
    this.translateY = 0;
    this.startY = 0;
    this.currentY = 0;

    const sheetElement = this.getSheetElement();
    if (sheetElement) {
      sheetElement.style.transition = '';
      sheetElement.style.transform = '';
      sheetElement.style.opacity = '';
    }

    const backdropElement = document.querySelector(
      '.bottom-sheet-backdrop'
    ) as HTMLElement;
    if (backdropElement) {
      backdropElement.style.transition = '';
      backdropElement.style.opacity = '';
    }
  }
}
